<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <span class="flex-grow-1" v-if="name"><b>{{ $t('oliveOilIntro') }}</b> {{name}}</span>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <div class="flex-column-responsive gap-1">
                    <!-- Photo -->
                    <div role="group" class="form-group p-3">
                        <GalleryPicker class="gallery-picker" v-model="oliveOil.photo"
                            :ratioWidth="ratioWidth"
                            :ratioHeight="ratioHeight"
                            @update:ratioWidth="ratioWidth = $event"
                            @update:ratioHeight="ratioHeight = $event"
                            v-bind:class="{'is-invalid': genericValidator(oliveOil.photo) === false || errorValidator('Photo') === false }" 
                            :max="1">
                        </GalleryPicker>
                    </div>
                    <div class="d-flex flex-wrap flex-grow-1">
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('positionOptional') }}</label>
                            <CInput type="number" v-model="oliveOil.position" :invalid-feedback="errorOutput['Position']"
                            :is-valid="errorValidator('Position')"></CInput>
                        </div>
                        <!-- Names -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('nameMandatory') }}</label>
                            <VueEditor v-model="oliveOil.names[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(oliveOil.names) == false || errorValidator('Names') }"></VueEditor>
                            <div v-if="errorOutput['Names']" class="invalid-feedback">{{errorOutput['Names']}}</div>
                        </div>
                        <!-- Consumptions temporary changed to expirationDate without changing the BE-->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class=""> {{ $t('expirationDate') }}</label>
                            <CInput type="text" v-model="oliveOil.consumptions[currentLanguage.key]" :invalid-feedback="errorOutput['Consumptions']" :is-valid="genericValidator(oliveOil.consumptions) && errorValidator('Temperatures')"></CInput>
                        </div>
                        <!-- Temperatures temporary changed to taste without changing the BE -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class=""> {{ $t('taste') }}</label>
                            <CInput type="text" v-model="oliveOil.temperatures[currentLanguage.key]" :invalid-feedback="errorOutput['Temperatures']" :is-valid="genericValidator(oliveOil.temperatures) && errorValidator('Temperatures')"></CInput>
                        </div>
                        <!-- Castes temporary changed to origin without changing the BE -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class=""> {{ $t('origin') }}</label>
                            <CInput type="text" v-model="oliveOil.castes[currentLanguage.key]" :invalid-feedback="errorOutput['Castes']" :is-valid="genericValidator(oliveOil.castes) && errorValidator('Castes')"></CInput>
                        </div>
                        <!-- Stages temporary changed to variety without changing the BE -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class=""> {{ $t('variety') }}</label>
                            <CInput type="text" v-model="oliveOil.stages[currentLanguage.key]" :invalid-feedback="errorOutput['Stages']" :is-valid="genericValidator(oliveOil.stages) && errorValidator('Stages')"></CInput>
                        </div>
                        <!-- Datasheet -->
                        <CustomPicker 
                            v-for="language in supportedLanguages" 
                            :key="`datasheet-${language.key}`" 
                            v-show="language.key == currentLanguage.key" 
                            :identifier="language.key" 
                            :title="$t('datasheet')" 
                            class="form-group col-12 col-lg-6" 
                            v-model="oliveOil.datasheets[language.key]" 
                            v-bind:class="{'is-invalid': errorValidator('Datasheets')}">
                        </CustomPicker>
                    </div>
                </div>
                <!-- Description -->
                <div role="group" class="form-group col-12">
                    <label class=""> {{ $t('descriptionMandatory') }}</label>
                    <VueEditor v-model="oliveOil.descriptions[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(oliveOil.descriptions) == false || errorValidator('Descriptions') }"></VueEditor>
                    <div v-if="errorOutput['Descriptions']" class="invalid-feedback">{{errorOutput['Descriptions']}}</div>
                </div>
                <!-- Action button + submitting -->  
                <div role="group" class="form-group col-12 d-flex justify-content-end">
                    <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                    <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                </div>
                <!-- submitting -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import OliveOil from "@/classes/OliveOil";
import { mapState } from 'vuex'
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import GalleryPicker from '@/controls/GalleryPicker.vue'
import { VueEditor } from "vue2-editor";
import CustomPicker from '@/controls/CustomPicker.vue'
import FormLoading from '@/controls/FormLoading.vue'
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import { API_HELPER } from "@/helpers/api.js";

  export default {
  components: { LanguageDropdown, ConfirmationPopUp, GalleryPicker, VueEditor, CustomPicker, FormLoading },
    props:{    
        baseOliveOil:
        {
            type: Object,
            default: null,
        },
        baseOliveOilName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            oliveOil: new OliveOil({}, {}, {}, {}, {}, {}, {}, []),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            ratioWidth: 2, // default ratio width
            ratioHeight: 7,  // default ratio height
        }
    },
    mounted: function()
    {
        this.resetOliveOil();
    },
    computed:
    {
        supportedLanguages: function()
        {
            return CONSTANTS.supportedLanguages;
        },
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = [];

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate multilanguage fields
            for (const [key, value] of Object.entries(this.oliveOil.names)) { if(!value) { result['Names'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.oliveOil.descriptions)) { if(!value) { result['Descriptions'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.oliveOil.consumptions)) { if(!value) { result['Consumptions'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.oliveOil.temperatures)) { if(!value) { result['Temperatures'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.oliveOil.castes)) { if(!value) { result['Castes'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.oliveOil.stages)) { if(!value) { result['Stages'] = this.$i18n.t("validateBothTranslations"); break; }; }

            return result;
        },
        ...mapState('oliveOils', ['postOliveOilOutput']),
        ...mapState('oliveOils', ['putOliveOilOutput']),
        name: function()
        {
            return this.baseOliveOil || this.baseOliveOilName ? (this.baseOliveOil ? this.baseOliveOil.name : this.baseOliveOilName) : null;
        },
        aspectRatio() {
            return (this.ratioWidth / this.ratioHeight).toFixed(2);
        },
    },
    methods:{
        resetOliveOil(){
            if(!this.baseOliveOil)
            {
                var names = {}; var descriptions = {}; var consumptions = {}; var temperatures = {}; var castes = {}; var stages = {}; var datasheets = {};
                CONSTANTS.supportedLanguages.forEach(language => { names[language.key] = ""; descriptions[language.key] = ""; consumptions[language.key] = ""; temperatures[language.key] = ""; castes[language.key] = ""; stages[language.key] = ""; datasheets[language.key] = null;  });

                this.oliveOil = new OliveOil(names, descriptions, consumptions, temperatures, castes, stages, datasheets, []);
            }
            else
            {
                this.oliveOil = deepClone(new OliveOil, this.baseOliveOil);    
                this.oliveOil.photo = {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.oliveOil.photoPath])};
            }
        },
        submitClicked(){
            this.triedSubmit = true;
            // Emit event
            if(this.oliveOil.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.oliveOil);
            }
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            this.$router.push({name: "OliveOilList" });
        },
        genericValidator,
        errorValidator
    },
    watch:
    {
        postOliveOilOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        putOliveOilOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseOliveOil: function()
        {
            this.resetOliveOil();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

    .showDropdown
    {
        display: block !important;
    }

    .input-group-append
    {
      height: fit-content;
    }

    img
    {
        width: 50%;
        height: 50%;
    }

</style> 